/**
 * The widget settings surface (`specs/M10-D2-widgets.md` §5, card **P-181**).
 *
 * Until this existed a chart could be added and never told what to chart: every widget on every
 * dashboard ran on `defaultSettings` while `dashboards.updateWidget`, `dashboards.settingsOptions`
 * and each kind's zod `settingsSchema` all existed and nothing called them.
 *
 * **The layout is ours, and says so.** `discovery/ui-reference/dashboards/INDEX.md` §Blocked
 * records that this account's five widget views are app iframes, so monday's own pane — where it
 * sits, what it groups, whether the chart's board and column pickers are one list or two — was
 * never captured (**q536**). Nothing here is presented as measured: it is the **`settingsSchema`
 * rendered**, one control per key the kind declares, in the order the schema declares them, over
 * the registry's own answer to "which columns may I group by / measure" — so a new widget kind
 * adds a form beside its own entry and nothing outside this file branches on a kind
 * (CLAUDE.md rule 6).
 */
import {
  COLUMN_AGGREGATIONS,
  COUNT_MEASURE_KEY,
  DATE_BUCKETS,
  GRAPH_TYPES,
  GROUP_DIMENSION_KEY,
  activeAxisKeys,
  type ColumnAggregation,
} from '@elizmonday/shared';
import type { CanvasColumnOption, CanvasSettingsOptions, WidgetRow } from '../types';

export interface WidgetSettingsFormProps {
  widget: WidgetRow;
  options: CanvasSettingsOptions | undefined;
  /** Merged into the widget's settings and written through `dashboards.updateWidget`. */
  onChange(patch: Record<string, unknown>): void;
  /**
   * The id the form's root renders, declared so the **call site** may state it too (P-189):
   * the coverage ledger reads literal `data-testid` attributes off JSX.
   */
  'data-testid'?: string;
}

type WidgetSettingsForm = (props: WidgetSettingsFormProps) => React.ReactElement;

/* ── shared controls ──────────────────────────────────────────────────────── */

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="ez-widget-settings__field">
      <span className="ez-widget-settings__label">{label}</span>
      {children}
    </label>
  );
}

function columnLabel(column: CanvasColumnOption, multiBoard: boolean): string {
  return multiBoard ? `${column.title} · ${column.boardId}` : column.title;
}

/** The boards a widget draws from; empty means "every board this canvas connects" (D2 §5). */
function BoardPicker({
  widget,
  options,
  onChange,
}: WidgetSettingsFormProps & { widget: WidgetRow }) {
  const boards = options?.boards ?? [];
  const selected = Array.isArray(widget.settings['boardIds']) ? (widget.settings['boardIds'] as string[]) : [];
  if (boards.length <= 1) return null;
  return (
    <fieldset className="ez-widget-settings__field" data-testid="widget-settings-boards">
      <legend className="ez-widget-settings__label">Boards</legend>
      {boards.map((board) => (
        <label key={board.id} className="ez-widget-settings__check">
          <input
            type="checkbox"
            checked={selected.length === 0 || selected.includes(board.id)}
            data-testid={`widget-settings-board-${board.id}`}
            onChange={(event) => {
              const base = selected.length === 0 ? boards.map((entry) => entry.id) : selected;
              const next = event.target.checked
                ? [...new Set([...base, board.id])]
                : base.filter((id) => id !== board.id);
              onChange({ boardIds: next.length === boards.length ? [] : next });
            }}
          />
          <span>{board.name}</span>
        </label>
      ))}
    </fieldset>
  );
}

/* ── chart ────────────────────────────────────────────────────────────────── */

const ChartSettingsForm: WidgetSettingsForm = (props) => {
  const { widget, options, onChange } = props;
  const settings = widget.settings;
  const dimensions = options?.dimensionColumns ?? [];
  const measures = options?.measureColumns ?? [];
  const multiBoard = (options?.boards ?? []).length > 1;
  const xKey = activeAxisKeys((settings['xAxisColumns'] ?? {}) as Record<string, boolean>)[0] ?? '';
  const yKey = activeAxisKeys((settings['yAxisColumns'] ?? {}) as Record<string, boolean>)[0] ?? COUNT_MEASURE_KEY;

  return (
    <div className="ez-widget-settings__form" data-testid="widget-settings-form">
      <Field label="Chart type">
        <select
          value={String(settings['graphType'] ?? 'column')}
          data-testid="widget-settings-graph-type"
          onChange={(event) => onChange({ graphType: event.target.value })}
        >
          {GRAPH_TYPES.map((type) => (
            <option key={type} value={type}>
              {type.replace(/_/g, ' ')}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Group by">
        <select
          value={xKey}
          data-testid="widget-settings-dimension"
          onChange={(event) => onChange({ xAxisColumns: event.target.value === '' ? {} : { [event.target.value]: true } })}
        >
          <option value="">Nothing (one bar)</option>
          <option value={GROUP_DIMENSION_KEY}>Group</option>
          {dimensions.map((column) => (
            <option key={`${column.boardId}:${column.id}`} value={column.id}>
              {columnLabel(column, multiBoard)}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Measure">
        <select
          value={yKey}
          data-testid="widget-settings-measure"
          onChange={(event) => onChange({ yAxisColumns: { [event.target.value]: true } })}
        >
          <option value={COUNT_MEASURE_KEY}>Count of items</option>
          {measures.map((column) => (
            <option key={`${column.boardId}:${column.id}`} value={column.id}>
              {columnLabel(column, multiBoard)}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Aggregation">
        <select
          value={String(settings['calcFunctionType'] ?? 'sum')}
          data-testid="widget-settings-aggregation"
          disabled={yKey === COUNT_MEASURE_KEY}
          onChange={(event) => onChange({ calcFunctionType: event.target.value })}
        >
          {COLUMN_AGGREGATIONS.filter((func) => SCALAR_AGGREGATIONS.has(func)).map((func) => (
            <option key={func} value={func}>
              {func}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Bucket dates by">
        <select
          value={String(settings['groupBy'] ?? 'month')}
          data-testid="widget-settings-date-bucket"
          onChange={(event) => onChange({ groupBy: event.target.value })}
        >
          {DATE_BUCKETS.map((bucket) => (
            <option key={bucket} value={bucket}>
              {bucket}
            </option>
          ))}
        </select>
      </Field>

      <label className="ez-widget-settings__check">
        <input
          type="checkbox"
          checked={settings['emptyValues'] === true}
          data-testid="widget-settings-empty-values"
          onChange={(event) => onChange({ emptyValues: event.target.checked })}
        />
        <span>Show items with no value</span>
      </label>

      <BoardPicker {...props} />
    </div>
  );
};

/** The aggregations that answer one number; the rest are footer renderings, not measures. */
const SCALAR_AGGREGATIONS = new Set<ColumnAggregation>([
  'count',
  'count_filled',
  'count_empty',
  'sum',
  'avg',
  'min',
  'max',
  'median',
]);

/* ── numbers ──────────────────────────────────────────────────────────────── */

const NumbersSettingsForm: WidgetSettingsForm = (props) => {
  const { widget, options, onChange } = props;
  const settings = widget.settings;
  const measures = options?.measureColumns ?? [];
  const multiBoard = (options?.boards ?? []).length > 1;
  const columnId = settings['columnId'] === null || settings['columnId'] === undefined ? '' : String(settings['columnId']);

  return (
    <div className="ez-widget-settings__form" data-testid="widget-settings-form">
      <Field label="Column">
        <select
          value={columnId}
          data-testid="widget-settings-column"
          onChange={(event) =>
            onChange({
              columnId: event.target.value === '' ? null : event.target.value,
              // Count of items is the only aggregation a widget with no column can answer.
              ...(event.target.value === '' ? { func: 'count' } : {}),
            })
          }
        >
          <option value="">Number of items</option>
          {measures.map((column) => (
            <option key={`${column.boardId}:${column.id}`} value={column.id}>
              {columnLabel(column, multiBoard)}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Aggregation">
        <select
          value={String(settings['func'] ?? 'count')}
          data-testid="widget-settings-aggregation"
          disabled={columnId === ''}
          onChange={(event) => onChange({ func: event.target.value })}
        >
          {COLUMN_AGGREGATIONS.filter((func) => SCALAR_AGGREGATIONS.has(func)).map((func) => (
            <option key={func} value={func}>
              {func}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Unit">
        <input
          type="text"
          value={settings['unit'] === null || settings['unit'] === undefined ? '' : String(settings['unit'])}
          placeholder="none"
          data-testid="widget-settings-unit"
          onChange={(event) => onChange({ unit: event.target.value === '' ? null : event.target.value })}
        />
      </Field>

      <BoardPicker {...props} />
    </div>
  );
};

/* ── battery ──────────────────────────────────────────────────────────────── */

const BatterySettingsForm: WidgetSettingsForm = (props) => {
  const { widget, options, onChange } = props;
  const settings = widget.settings;
  const dimensions = options?.dimensionColumns ?? [];
  const multiBoard = (options?.boards ?? []).length > 1;

  return (
    <div className="ez-widget-settings__form" data-testid="widget-settings-form">
      <Field label="Column">
        <select
          value={settings['columnId'] === null || settings['columnId'] === undefined ? '' : String(settings['columnId'])}
          data-testid="widget-settings-column"
          onChange={(event) => onChange({ columnId: event.target.value === '' ? null : event.target.value })}
        >
          <option value="">Pick a column</option>
          {dimensions.map((column) => (
            <option key={`${column.boardId}:${column.id}`} value={column.id}>
              {columnLabel(column, multiBoard)}
            </option>
          ))}
        </select>
      </Field>

      <label className="ez-widget-settings__check">
        <input
          type="checkbox"
          checked={settings['showPercentage'] !== false}
          data-testid="widget-settings-show-percentage"
          onChange={(event) => onChange({ showPercentage: event.target.checked })}
        />
        <span>Show the percentage</span>
      </label>

      <BoardPicker {...props} />
    </div>
  );
};

/* ── text ─────────────────────────────────────────────────────────────────── */

const TextSettingsForm: WidgetSettingsForm = ({ widget, onChange }) => (
  <div className="ez-widget-settings__form" data-testid="widget-settings-form">
    <Field label="Text">
      <textarea
        rows={6}
        value={String(widget.settings['html'] ?? '')}
        data-testid="widget-settings-html"
        onChange={(event) => onChange({ html: event.target.value })}
      />
    </Field>
  </div>
);

/* ── the registry of forms ────────────────────────────────────────────────── */

/**
 * Keyed by the registry id, so the canvas asks "does this kind have a settings form?" rather
 * than "which kind is this?". A kind with no entry gets the honest empty state below.
 */
export const WIDGET_SETTINGS_FORMS: Readonly<Record<string, WidgetSettingsForm>> = {
  chart: ChartSettingsForm,
  numbers: NumbersSettingsForm,
  battery: BatterySettingsForm,
  text: TextSettingsForm,
};

export function widgetSettingsForm(registryId: string): WidgetSettingsForm | null {
  return WIDGET_SETTINGS_FORMS[registryId] ?? null;
}
